import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sun, Moon, ChevronDown, LogOut, User, MessageCircle, Users, AtSign, Check, MapPin, Crown, Building2 } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { authedUrl, api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AskPrizmPalette from "@/components/AskPrizmPalette";
import { useBranches } from "@/components/BranchBits";
import { useBranchView } from "@/context/BranchViewContext";

/** Company pill → branch view switcher.
 *  Company Admin: "All Branches" first, then every branch.
 *  Branch Admin: their own branch is auto-selected (listed first); other branches can be browsed read-only.
 *  Everyone else keeps the plain company pill. */
function BranchSwitcher() {
  const { user } = useAuth();
  const { branches } = useBranches();
  const { selected, setSelected } = useBranchView();
  const isCompanyAdmin = user?.role === "admin";
  const isBranchAdmin = user?.role === "branch_admin";
  const active = branches.filter((b) => b.is_active !== false);
  const current = active.find((b) => b.id === selected);
  const label = isCompanyAdmin && (selected === "all" || !selected) ? "All Branches" : (current?.name || (selected === "all" ? "All Branches" : "Simpel"));

  const pill = (
    <div data-testid="branch-switcher" className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg hairline cursor-default">
      {/* Same icons as the dropdown rows: All Branches → people, a branch → map pin */}
      {isCompanyAdmin && (selected === "all" || !selected)
        ? <Users className="w-4 h-4 text-muted-foreground shrink-0" data-testid="branch-switcher-icon" />
        : <MapPin className="w-4 h-4 text-muted-foreground shrink-0" data-testid="branch-switcher-icon" />}
      <span className="text-sm font-medium max-w-[160px] truncate" data-testid="branch-switcher-label">{label}</span>
      {(isCompanyAdmin || isBranchAdmin) && <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
    </div>
  );
  if (!isCompanyAdmin && !isBranchAdmin) {
    return (
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg hairline">
        <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium">Simpel</span>
      </div>
    );
  }
  const own = isBranchAdmin ? active.find((b) => b.id === user.branch_id) : null;
  const others = active.filter((b) => !own || b.id !== own.id);
  const Item = ({ id, children, testid }) => (
    <DropdownMenuItem data-testid={testid} onClick={() => setSelected(id)} className="gap-2 cursor-pointer">
      <span className="flex-1 flex items-center gap-2 min-w-0">{children}</span>
      {selected === id && <Check className="w-4 h-4 text-primary" />}
    </DropdownMenuItem>
  );
  const BranchRow = ({ b }) => (
    <Item id={b.id} testid={`branch-opt-${b.id}`}>
      <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="truncate">{b.name}</span>
      {b.city && b.city !== b.name && <span className="text-[10px] text-muted-foreground truncate">{b.city}</span>}
      {b.is_head_office && <Crown className="w-3 h-3 text-primary shrink-0" title="Head Office" />}
    </Item>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild><button type="button" className="outline-none text-left">{pill}</button></DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64" data-testid="branch-menu">
        <DropdownMenuLabel className="text-[10px] label-caps">Branch view</DropdownMenuLabel>
        {isCompanyAdmin && (
          <Item id="all" testid="branch-opt-all"><Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />All Branches</Item>
        )}
        {own && (<><BranchRow b={own} />{others.length > 0 && <DropdownMenuSeparator />}</>)}
        {isBranchAdmin && others.length > 0 && <DropdownMenuLabel className="text-[10px] label-caps">Other branches · view only</DropdownMenuLabel>}
        {isCompanyAdmin && active.length > 0 && <DropdownMenuSeparator />}
        {others.map((b) => <BranchRow key={b.id} b={b} />)}
        {active.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">No branches yet — add one under People → Branches</div>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function TopNav({ onOpenAI }) {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [query, setQuery] = useState("");
  const [unread, setUnread] = useState(0);
  const [convs, setConvs] = useState([]);
  const nav = useNavigate();

  // Fetch unread chat count on mount, on window focus, every 30s, and whenever
  // the ChatNotifier / Chat page dispatches a `chat-unread-changed` event.
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/chat/conversations", { params: { include_projects: true } });
        if (cancelled) return;
        const rows = r.data || [];
        setConvs(rows);
        setUnread(rows.reduce((sum, c) => sum + (c.unread || 0), 0));
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    const onCustom = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    window.addEventListener("chat-unread-changed", onCustom);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("chat-unread-changed", onCustom);
    };
  }, []);

  const initials = (user?.full_name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");

  return (
    <header
      data-testid="app-topnav"
      className="sticky top-0 z-30 glass-strong border-b h-16 flex items-center px-4 lg:px-8 gap-4"
    >
      {/* Company / branch switcher */}
      <BranchSwitcher />

      {/* Global search — opens the Ask PRIZM palette (also triggered by ⌘K) */}
      <div className="relative flex-1 max-w-xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="global-search"
          placeholder="Ask PRIZM — docs, CRs, tasks, people…"
          className="pl-9 h-10 bg-secondary/60 border-transparent focus-visible:border-primary/40 cursor-pointer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            // Open the palette on focus; `window.dispatchEvent` triggers the
            // same keyboard-shortcut path AskPrizmPalette already listens to.
            const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
            window.dispatchEvent(ev);
            setQuery("");
            e.target.blur();
          }}
          readOnly
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 hidden md:flex">
          <kbd className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-background hairline text-muted-foreground">⌘K</kbd>
        </div>
      </div>
      <AskPrizmPalette />

      <div className="flex-1" />

      {/* AI Chat Button */}
      <Button
        data-testid="topnav-ai-btn"
        onClick={onOpenAI}
        variant="ghost"
        className="ai-aura relative rounded-full px-4 h-10 bg-background hover:bg-background"
      >
        <span className="relative z-10 flex items-center gap-2 text-sm">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          Ask AI
        </span>
      </Button>

      {/* Theme */}
      <Button
        data-testid="theme-toggle"
        onClick={toggle}
        variant="ghost"
        size="icon"
        className="rounded-full"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </Button>

      {/* Chat notifications */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="chat-inbox-btn"
            variant="ghost"
            size="icon"
            className="rounded-full relative"
            aria-label="Chat notifications"
            title="Chat notifications"
          >
            <MessageCircle className="w-4 h-4" />
            {unread > 0 && (
              <Badge
                data-testid="chat-unread-badge"
                className="absolute -top-1 -right-1 h-4 min-w-4 px-1 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px]"
              >
                {unread > 99 ? "99+" : unread}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[380px] p-0" data-testid="chat-notif-panel">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="font-display font-semibold">Notifications</div>
            {unread > 0 && <span className="text-xs text-muted-foreground">{unread} unread</span>}
          </div>
          <div className="max-h-[420px] overflow-y-auto scroll-thin">
            {convs.filter((c) => c.last_message).length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">No messages yet</div>
            )}
            {convs.filter((c) => c.last_message).slice(0, 20).map((c) => {
              const isProject = c.type === "project";
              const isGroup = c.type === "group";
              const name = c.title || c.other?.full_name || "Chat";
              const preview = (isProject || isGroup) && c.last_sender_name ? `${c.last_sender_name}: ${c.last_message}` : c.last_message;
              return (
                <DropdownMenuItem
                  key={c.id}
                  data-testid={`notif-${c.id}`}
                  onClick={() => nav(isProject ? `/projects/${c.project_id}?tab=discussion` : `/chat?c=${c.id}`)}
                  className={`px-4 py-3 gap-3 rounded-none cursor-pointer items-start border-b last:border-b-0 ${c.unread > 0 ? "bg-primary/5" : ""}`}
                >
                  {isProject || isGroup ? (
                    <div className="h-9 w-9 shrink-0 rounded-full bg-primary/15 text-primary flex items-center justify-center"><Users className="w-4 h-4" /></div>
                  ) : (
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={authedUrl(c.other?.avatar_url)} />
                      <AvatarFallback className="text-[10px]">{(name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className={`text-sm truncate ${c.unread > 0 ? "font-semibold" : "font-medium"}`}>
                        {name}{isProject && <span className="text-muted-foreground font-normal"> · Discussion</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground shrink-0">
                        {c.last_message_at ? new Date(c.last_message_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <div className="text-xs text-muted-foreground truncate">{preview}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        {c.mention_unread > 0 && <AtSign className="w-3.5 h-3.5 text-primary" data-testid={`notif-mention-${c.id}`} />}
                        {c.unread > 0 && <Badge className="h-4 min-w-4 px-1 text-[9px]">{c.unread}</Badge>}
                      </div>
                    </div>
                  </div>
                </DropdownMenuItem>
              );
            })}
          </div>
          <DropdownMenuItem data-testid="notif-open-chat" onClick={() => nav("/chat")} className="justify-center rounded-none py-2.5 text-sm text-primary cursor-pointer border-t">
            Open all chats
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="user-menu-trigger" variant="ghost" className="rounded-full pl-1 pr-3 gap-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={authedUrl(user?.avatar_url)} />
              <AvatarFallback className="bg-primary/15 text-primary text-xs">{initials}</AvatarFallback>
            </Avatar>
            <div className="text-left hidden md:block">
              <div className="text-sm font-medium leading-tight">{user?.full_name}</div>
              <div className="text-[10px] label-caps -mt-0.5">{user?.role}</div>
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="profile-menu-item" onClick={() => nav("/settings")}>
            <User className="w-4 h-4 mr-2" /> Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-destructive">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
